import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { AiMessageStatusEnum } from 'graphql-common';

// Several distinct BE error codes intentionally collapse onto one user-facing
// message (e.g. server_error / configuration_error / completion_failed all read
// as a generic server error). Declaring the mapping message-first keeps each
// translation key on a single line, then this flattens it into the code→key
// lookup the getters need.
const flattenErrorKeys = (
  codesByKey: Record<string, readonly string[]>,
): Record<string, string> =>
  Object.entries(codesByKey).reduce<Record<string, string>>(
    (acc, [key, codes]) => {
      codes.forEach((code) => {
        acc[code] = key;
      });
      return acc;
    },
    {},
  );

// Maps a failed/truncated assistant message's errorCode (carried on both the
// terminal stream frame and the persisted message) to user-facing copy.
// See datizy-api/docs/fe/ai_frontend_guide.md §6 for the authoritative code list.
const MESSAGE_ERROR_KEY_BY_CODE = flattenErrorKeys({
  'ai-error-budget-not-configured': ['budget_not_configured'],
  'ai-error-budget-exhausted': ['budget_exhausted'],
  'ai-error-access-revoked': ['plan_revoked', 'access_revoked', 'gate_failed'],
  'ai-error-attachment-unauthorized': ['attachment_unauthorized'],
  'ai-error-file-missing': ['file_missing'],
  'ai-error-tool-limit': ['tool_call_limit_exceeded'],
  'ai-error-truncated-budget': ['conversation_too_long'],
  'ai-error-rate-limited': ['rate_limited'],
  'ai-error-server': [
    'server_error',
    'configuration_error',
    'completion_failed',
  ],
});

// The copy for a bare error code, with no message status around it. Background
// jobs share this vocabulary with the AI stream (`BackgroundJob.errorCode` —
// budget_exhausted, access_revoked, rate_limited, …), so a failed job says WHY
// it failed in the same words the chat uses. Null for an unclassified crash:
// the caller falls back to its own generic failure copy rather than inventing a
// reason.
export function getAiErrorCodeText(
  t: TFunction<'translation', undefined>,
  errorCode?: string | null,
): string | null {
  const key = (errorCode && MESSAGE_ERROR_KEY_BY_CODE[errorCode]) || null;
  return key ? t(key) : null;
}

// Codes whose outcome is already fully expressed by the message body, so an
// error line beneath it would be noise (or an outright contradiction):
//   stopped_by_user   — the user's own Stop. The partial text IS the result;
//                       guide §6 says keep it and show no error UX.
//   injection_blocked — the BE replaced the leaked reply with a localized
//                       refusal and persists it as the content, so the bubble
//                       already explains itself.
const SILENT_ERROR_CODES = ['stopped_by_user', 'injection_blocked'];

export function getAiMessageErrorText(
  t: TFunction<'translation', undefined>,
  status?: AiMessageStatusEnum | null,
  errorCode?: string | null,
): string | null {
  if (errorCode && SILENT_ERROR_CODES.includes(errorCode)) return null;
  if (status === AiMessageStatusEnum.TruncatedBudget) {
    return t('ai-error-truncated-budget');
  }
  // A server-side cancel (distinct from the client-side Stop, which carries
  // stopped_by_user and is silenced above) ends the turn with no answer —
  // surface a neutral notice so the user's message isn't dropped without
  // explanation (guide §4).
  if (status === AiMessageStatusEnum.Cancelled) {
    return t('ai-cancelled');
  }
  if (status !== AiMessageStatusEnum.Failed) return null;
  const key = (errorCode && MESSAGE_ERROR_KEY_BY_CODE[errorCode]) || null;
  return key ? t(key) : t('ai-error-generic');
}

// Maps a synchronous aiSendMessage GraphQL error (top-level extensions.code)
// to a toast message.
const SEND_ERROR_KEY_BY_CODE: Record<string, string> = {
  AI_BUDGET_NOT_CONFIGURED: 'ai-error-budget-not-configured',
  AI_BUDGET_EXHAUSTED: 'ai-error-budget-exhausted',
  AI_ACCESS_REVOKED: 'ai-error-access-revoked',
  UPGRADE_PLAN_REQUIRED: 'ai-error-access-revoked',
  AI_SEND_MESSAGE_RATE_LIMITED: 'ai-error-rate-limited',
};

export function getAiSendErrorText(
  t: TFunction<'translation', undefined>,
  error: unknown,
): string {
  const isGraphQLError = CombinedGraphQLErrors.is(error);
  const code = (isGraphQLError ? error.errors : [])
    .map(({ extensions }) => _get(extensions, 'code') as string | undefined)
    .find((value) => value && SEND_ERROR_KEY_BY_CODE[value]);
  const key = code ? SEND_ERROR_KEY_BY_CODE[code] : null;
  if (key) return t(key);
  // No GraphQL error code means the request never reached the server (offline,
  // DNS failure, etc.) — say so instead of the generic server-error copy.
  if (!isGraphQLError && error) return t('ai-error-network');
  return t('ai-error-generic');
}
